import { FC, useEffect, useState } from "react";

import {
  BasketCombinedSortType,
  BasketSortDirection,
  BasketsSort,
  useOpenCreateBasket,
} from "@keepeek/commons";
import {
  BaseComponentProps,
  KButton,
  SanitizeHtml,
  useThemeColors,
  withSuspense,
} from "@keepeek/refront-components";
import { Box } from "@mui/material";
import * as Sentry from "@sentry/nextjs";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { useRecoilState } from "recoil";

import { BasketsWidget as BasketsWidgetModel } from "../../../models/configuration/components/basketsWidget";
import { useCurrentLocale } from "../../../providers/i18n/hooks";
import { WidgetUIState } from "../atoms";
import { useWidgetState } from "../hooks";
import { KWidgetProps } from "../models";
import { currentPageBaskets, currentSizeBaskets } from "./atoms";
import BasketsTotalCount from "./BasketsTotalCount";
import BasketsWidgetActions from "./BasketsWidgetActions";
import BasketsWidgetList from "./BasketsWidgetList";
import BasketsWidgetPaginate from "./BasketsWidgetPaginate";

export type BasketsWidgetProps = {
  widgetId: string;
} & Omit<BasketsWidgetModel, "id"> &
  BaseComponentProps &
  KWidgetProps;

const BasketsWidget: FC<BasketsWidgetProps> = function ({
  type,
  widgetId,
  backgroundColor,
  create,
  order,
  paginate,
  search,
  tip,
  title,
}) {
  const { t } = useTranslation();

  const [page, setPage] = useRecoilState(currentPageBaskets);
  const [size, setSize] = useRecoilState(currentSizeBaskets);

  const sentryCallback = ({ category, message }) => Sentry.addBreadcrumb({ category, message });
  const currentLocale = useCurrentLocale();

  const openCreateBasket = useOpenCreateBasket(sentryCallback);

  const { setWidgetState } = useWidgetState({
    id: widgetId,
    type,
  });
  useEffect(() => {
    setWidgetState({ state: WidgetUIState.RENDERED });
  }, [setWidgetState]);

  const currentTip = tip?.find((t) => t.language === currentLocale) ?? {
    value: t("basketWidget.tip.default.label"),
    empty: false,
  };

  const { convertColor } = useThemeColors();
  const [queryFilter, setQueryFilter] = useState("");
  const [sort, setSort] = useState(
    `${BasketsSort.CreationDate} ${BasketSortDirection.DSC}` as BasketCombinedSortType,
  );

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        justifyContent: "center",
        flexDirection: "column",
        backgroundColor: convertColor(backgroundColor),
      }}
      className={clsx("BasketsWidget")}
      gap={4}
    >
      <BasketsTotalCount widgetId={widgetId} title={title} queryFilter={queryFilter} />
      <BasketsWidgetActions
        widgetId={widgetId}
        order={order}
        search={search}
        size={size}
        page={page}
        setPage={setPage}
        sort={sort}
        setSort={setSort}
        queryFilter={queryFilter}
        setQueryFilter={setQueryFilter}
      />
      <BasketsWidgetList
        widgetId={widgetId}
        sort={sort}
        page={page}
        size={size}
        queryFilter={queryFilter}
        setQueryFilter={setQueryFilter}
      />
      <BasketsWidgetPaginate
        widgetId={widgetId}
        paginate={paginate}
        page={page}
        setPage={setPage}
        size={size}
        setSize={setSize}
        queryFilter={queryFilter}
      />
      <Box
        sx={{
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          mb: 4,
        }}
        display="flex"
        gap={2}
      >
        {(create?.show ?? true) && (
          <Box>
            <KButton onClick={openCreateBasket} variant="contained" buttonName="createBasket">
              {t("page.baskets.create.button.label")}
            </KButton>
          </Box>
        )}
        {currentTip && !currentTip.empty && (
          <Box textAlign="center" className={clsx("BasketsWidget-tip")}>
            <SanitizeHtml html={currentTip.value ?? ""} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default withSuspense(BasketsWidget);
